import { Activity, CandlestickChart, TrendingDown, TrendingUp, Waves, Zap } from "lucide-react";
import ItfChart from "@/components/ItfChart";
import Reveal from "./Reveal";

const READS = [
    {
        icon: Activity,
        title: "Institutional activity",
        body: "ITF tracks the footprint that large institutional orders leave behind — where big money is quietly shaping the move before it becomes obvious.",
    },
    {
        icon: Waves,
        title: "Market behavior",
        body: "It reads how the market behaves around that activity: participation, momentum and reaction, measured through proprietary metrics.",
    },
    {
        icon: CandlestickChart,
        title: "Price structure",
        body: "It maps the structure of price itself — the levels where trends are born, where they continue, and where they break.",
    },
];

const SIGNAL_NOTES = [
    {
        icon: TrendingUp,
        tone: "emerald",
        title: "Green, below price",
        body: "Bullish flow is in control. The line trails underneath price and keeps rising while the upward trend develops.",
    },
    {
        icon: TrendingDown,
        tone: "rose",
        title: "Red, above price",
        body: "Bearish flow takes over. The line flips above price and holds there while the downward move plays out.",
    },
    {
        icon: Zap,
        tone: "slate",
        title: "Instant direction change",
        body: "The moment flow changes direction, the line changes side and color. No interpretation, no settings to tune — the chart simply shows you.",
    },
];

export default function Chapters() {
    return (
        <>
            <section id="how" className="relative py-20 sm:py-28 lg:py-32" data-testid="chapter-how">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <div className="max-w-3xl">
                            <p className="font-mono text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700">01 — What ITF reads</p>
                            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                                Serious analysis, <span className="text-balance">distilled into something a glance can read.</span>
                            </h2>
                            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                                Most indicators repackage price and call it insight. ITF starts somewhere else — with the
                                institutional activity that actually moves markets — and compresses everything into one
                                output you will genuinely use.
                            </p>
                        </div>
                    </Reveal>

                    <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
                        {READS.map((r, i) => (
                            <Reveal key={r.title} delay={i * 0.12}>
                                <div className="group h-full rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-xl">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-900 text-emerald-400 transition-transform duration-300 group-hover:scale-110">
                                        <r.icon className="h-5 w-5" />
                                    </span>
                                    <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-slate-900">{r.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{r.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.15}>
                        <div className="mt-10 rounded-2xl border border-emerald-200/70 bg-emerald-50/60 px-7 py-6">
                            <p className="text-base leading-relaxed text-slate-700 sm:text-lg">
                                <span className="font-display font-bold text-slate-900">The output is disarmingly simple:</span>{" "}
                                one line that turns <span className="font-semibold text-emerald-700">green</span> when the trend
                                is up and <span className="font-semibold text-rose-600">red</span> when it is down. If you can
                                read a color, you can read ITF.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <section id="demo" className="relative bg-white py-20 sm:py-28 lg:py-32" data-testid="chapter-demo">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <div className="flex flex-wrap items-end justify-between gap-6">
                            <div className="max-w-2xl">
                                <p className="font-mono text-xs font-semibold uppercase tracking-[0.24em] text-emerald-700">02 — ITF in action</p>
                                <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">
                                    One line. Every trend.
                                </h2>
                                <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                                    Watch ITF walk through bullish and bearish phases on a live-style market feed — holding
                                    the trend while it develops, and flipping the moment direction changes. ITF runs
                                    exclusively on the 5-minute intraday chart — focused by design.
                                </p>
                            </div>
                            <span
                                data-testid="tf-timeframe-5m-badge"
                                className="rounded-full bg-slate-900 px-5 py-2 font-mono text-xs font-semibold text-white shadow-md"
                            >
                                5-minute · intraday only
                            </span>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="chart-card-shadow mt-10 overflow-hidden rounded-2xl border border-slate-200/80 bg-white">
                            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
                                <div className="flex items-center gap-3">
                                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse-dot" />
                                    <span className="font-display text-sm font-bold tracking-tight text-slate-900">LIVE DEMO</span>
                                    <span className="hidden text-xs text-slate-500 sm:block">Simulated market session</span>
                                    <span className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-slate-600">5m</span>
                                </div>
                                <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                    Indicator preview · simulated data
                                </span>
                            </div>
                            <div className="px-2 py-2">
                                <ItfChart timeframe="5M" height={440} startDelay={200} />
                            </div>
                        </div>
                    </Reveal>

                    <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
                        {SIGNAL_NOTES.map((n, i) => (
                            <Reveal key={n.title} delay={i * 0.12}>
                                <div className="flex h-full gap-4 rounded-2xl border border-slate-200/80 bg-slate-50/60 p-6">
                                    <span
                                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
                                            n.tone === "emerald"
                                                ? "bg-emerald-100 text-emerald-700"
                                                : n.tone === "rose"
                                                  ? "bg-rose-100 text-rose-600"
                                                  : "bg-slate-900 text-emerald-400"
                                        }`}
                                    >
                                        <n.icon className="h-5 w-5" />
                                    </span>
                                    <div>
                                        <h4 className="font-display text-base font-bold tracking-tight text-slate-900">{n.title}</h4>
                                        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{n.body}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
}
